import { type JSX } from 'react';

import { EloIcon, Link as EloLink } from '@plurall/elo-v3';
import { Link } from '@tanstack/react-router';

import { clsx } from '@/lib/formatters/classNames';

import { type getDirectEntry } from '../helpers/localNavigationItemHelpers';
import {
  getFilledIconName,
  getIconColor,
  type LocalNavigationEntryItem,
  useLocalNavigationEntry,
} from '../hooks/useLocalNavigationEntry';
import { type LocalNavigationMenuItem } from '../hooks/useLocalNavigationMenuSections';
import { type UtilityAction } from '../types/localNavigationActions';
import {
  NavButtonFactory,
  NavLabelFactory,
  NavLinkFactory,
  NavSubitemLinkFactory,
} from './factories/local-navigation-entry-factory';

export type { LocalNavigationEntryItem };

type DirectEntrySource = NonNullable<ReturnType<typeof getDirectEntry>>;

function entryItemFromMenuAndDirect(
  menuItem: LocalNavigationMenuItem,
  directEntry: DirectEntrySource
): LocalNavigationEntryItem {
  return {
    title: directEntry.title ?? menuItem.title,
    text: directEntry.text ?? menuItem.text,
    icon: directEntry.icon ?? menuItem.icon ?? 'grid-squares',
    url: directEntry.url,
    targetEnvironment: directEntry.targetEnvironment ?? menuItem.targetEnvironment,
    fragment: directEntry.fragment,
    queryParams: directEntry.queryParams,
    queryParamsHandling: directEntry.queryParamsHandling,
    action: directEntry.action,
  };
}

type LocalNavigationEntrySharedProps = {
  active?: boolean;
  className: string;
  onAction?: (action: UtilityAction) => void;
  showText?: boolean;
};

export type LocalNavigationEntryProps =
  | (LocalNavigationEntrySharedProps & { item: LocalNavigationEntryItem })
  | (LocalNavigationEntrySharedProps & { directEntry: DirectEntrySource; menuItem: LocalNavigationMenuItem });

export function LocalNavigationEntry(props: LocalNavigationEntryProps): JSX.Element | null {
  const { active = false, className, onAction } = props;
  const item = 'directEntry' in props ? entryItemFromMenuAndDirect(props.menuItem, props.directEntry) : props.item;
  const { isHighlighted, routerLink, routerSearch, href, sharedProps } = useLocalNavigationEntry(item, active);
  const label = item.text || item.title || '';
  const iconColor = getIconColor();
  const shouldUseFilledIcon = item.icon === 'home' && label === 'Início';
  const resolvedIcon = shouldUseFilledIcon && isHighlighted ? getFilledIconName(item.icon) || item.icon : item.icon;

  // ─── Expanded mode ───────────────────────────────────────────────────────────
  const containerProps = {
    className: clsx(className, active && `${className}--active`),
    ...sharedProps,
  } as const;

  const content = (
    <>
      {resolvedIcon && (
        <EloIcon
          color={iconColor}
          data-test-id='elo-icon'
          icon={resolvedIcon}
          size='lg'
        />
      )}
      <NavLabelFactory>{label}</NavLabelFactory>
    </>
  );

  if (className === 'local-navigation__link') {
    const navLinkProps = {
      isActive: active,
      ...sharedProps,
    } as const;

    if (item.action) {
      return (
        <NavLinkFactory
          type='button'
          {...navLinkProps}
          onClick={() => onAction?.(item.action!)}>
          {content}
        </NavLinkFactory>
      );
    }

    if (routerLink) {
      return (
        <NavLinkFactory
          asChild
          tabIndex={0}
          {...navLinkProps}>
          <Link
            to={routerLink}
            search={routerSearch}
            hash={item.fragment}>
            {content}
          </Link>
        </NavLinkFactory>
      );
    }

    if (!href) return null;

    return (
      <NavLinkFactory
        asChild
        tabIndex={0}
        {...navLinkProps}>
        <EloLink href={href}>{content}</EloLink>
      </NavLinkFactory>
    );
  }

  if (className === 'local-navigation__subitem-link') {
    const subitemLinkProps = {
      isActive: active,
      ...sharedProps,
    } as const;

    if (item.action) {
      return (
        <NavSubitemLinkFactory
          type='button'
          {...subitemLinkProps}
          onClick={() => onAction?.(item.action!)}>
          {content}
        </NavSubitemLinkFactory>
      );
    }

    if (routerLink) {
      return (
        <NavSubitemLinkFactory
          asChild
          tabIndex={0}
          {...subitemLinkProps}>
          <Link
            to={routerLink}
            search={routerSearch}
            hash={item.fragment}>
            {content}
          </Link>
        </NavSubitemLinkFactory>
      );
    }

    if (!href) return null;

    return (
      <NavSubitemLinkFactory
        asChild
        tabIndex={0}
        {...subitemLinkProps}>
        <EloLink href={href}>{content}</EloLink>
      </NavSubitemLinkFactory>
    );
  }

  if (item.action) {
    return (
      <NavButtonFactory
        type='button'
        {...containerProps}
        onClick={() => onAction?.(item.action!)}>
        {content}
      </NavButtonFactory>
    );
  }

  if (routerLink) {
    return (
      <Link
        to={routerLink}
        search={routerSearch}
        hash={item.fragment}
        {...containerProps}>
        {content}
      </Link>
    );
  }

  if (!href) return null;

  return (
    <EloLink
      href={href}
      {...containerProps}>
      {content}
    </EloLink>
  );
}
